import { useState, useEffect } from 'react';
import { Save, Loader2 } from 'lucide-react';
import {
  AMBER, NAVY, BORDER_COLOR, BG_FIELD,
  bubbleStyle, navyBtn, fieldStyle,
} from '../../styles/tokens';
import client from '../../api/client';

const SETTING_GROUPS = [
  /* ⚰ V65 — "Aprobadores PTO" (pto_approver_l3/l4/l5_email) RETIRADO.
     Era la cadena FIJA de 5 niveles que V59 reemplazó por la cadena real de dos,
     resuelta desde la ganga del proyecto (Ingeniero → Socio). Los aprobadores ya no se
     configuran por correo en una pantalla: se derivan de `ganga_members`.

     ⚠ PEOR QUE INÚTIL, ERA ENGAÑOSO: los tres campos guardaban en `platform_settings`
     y NADIE los leía —verificado, cero consumidores en `backend/src`—, así que un admin
     podía escribir tres correos, guardarlos con éxito, y creer que había configurado
     quién aprueba. La cadena real seguía saliendo de la ganga.

     ⚠ Las filas siguen existiendo en `platform_settings` (las siembra `schema.sql`).
     Quedan huérfanas y son inocuas; limpiarlas es una pieza de datos aparte, no de UI. */
  {
    title: 'Recordatorios',
    description: 'Umbrales de tiempo para enviar recordatorios de aprobación pendiente',
    keys: [
      { key: 'pto_reminder_threshold_1', label: 'Primer recordatorio (horas)',  placeholder: '24' },
      { key: 'pto_reminder_threshold_2', label: 'Segundo recordatorio (horas)', placeholder: '48' },
      { key: 'pto_reminder_threshold_3', label: 'Tercer recordatorio (horas)',  placeholder: '72' },
    ],
  },
  {
    title: 'Correo O365 (SMTP)',
    description: 'Configuración de envío de correo vía Office 365',
    keys: [
      { key: 'o365_smtp_host',    label: 'Servidor SMTP',     placeholder: 'smtp.office365.com' },
      { key: 'o365_smtp_port',    label: 'Puerto',            placeholder: '587' },
      { key: 'o365_smtp_user',    label: 'Usuario (correo)',  placeholder: 'noreply@v2aconsulting.com' },
      { key: 'o365_smtp_pass',    label: 'Contraseña',        placeholder: '••••••••', type: 'password' },
      { key: 'o365_from_name',    label: 'Nombre remitente',  placeholder: 'V&A Platform' },
    ],
  },
  {
    title: 'Exportación Nómina (Sage)',
    description: 'Configuración del export CSV para Sage 50',
    keys: [
      {
        key: 'nomina_ticket_number_start',
        label: 'Número inicial de Ticket',
        placeholder: '90310',
        type: 'number',
        helper: 'El próximo export iniciará desde este número de forma secuencial.',
      },
    ],
  },
];

// VNA-128 — quién revisa las horas del personal de Oficina, sea cual sea el proyecto.
// Guarda CUENTAS con Revisión Final (el backend rechaza a cualquier otra); sin ninguna, las
// tarjetas de Oficina no se pueden someter.
const CLAVE_REVISORES_OFICINA = 'nomina_revisores_oficina';
function idsDe(value) {
  try { const v = JSON.parse(value || '[]'); return Array.isArray(v) ? v.map(Number) : []; } catch { return []; }
}

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [revisores, setRevisores] = useState([]);
  const [usuarios, setUsuarios] = useState([]);   // para nombrar a los guardados que ya no valen

  useEffect(() => {
    client.get('/api/admin/settings')
      .then(r => {
        setSettings(r.data || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    client.get('/api/nomina/users-by-role', { params: { role: 'reviewer' } })
      .then(r => setRevisores(r.data || []))
      .catch(() => setRevisores([]));
    client.get('/api/admin/users')
      .then(r => setUsuarios(r.data || []))
      .catch(() => setUsuarios([]));
  }, []);

  // Revisión final de la rama: un revisor guardado que después perdió el rol o se desactivó
  // ya no sale en la lista de Revisión Final, pero sigue en el ajuste — y el backend rechaza
  // guardar CUALQUIER ajuste mientras esté. Se muestra marcado y señalado para poder quitarlo.
  const revisoresVigentes = new Set(revisores.map(r => r.id));
  const yaNoValen = idsDe(settings[CLAVE_REVISORES_OFICINA])
    .filter(id => !revisoresVigentes.has(id))
    .map(id => ({ id, name: usuarios.find(u => u.id === id)?.name || `Usuario ${id}` }));

  function toggleRevisorOficina(id) {
    const actuales = idsDe(settings[CLAVE_REVISORES_OFICINA]);
    const nuevos = actuales.includes(id) ? actuales.filter(x => x !== id) : [...actuales, id];
    handleChange(CLAVE_REVISORES_OFICINA, JSON.stringify(nuevos));
  }

  function handleChange(key, value) {
    setSaved(false);
    setSettings(prev => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await client.put('/api/admin/settings', settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      alert('Error al guardar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
        <Loader2 size={20} className="spin" /> Cargando configuración…
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 28px', maxWidth: 800, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontSize: 20, fontWeight: '700', color: NAVY, margin: 0 }}>Configuración de la Plataforma</h2>
        <button onClick={handleSave} disabled={saving} style={{
          ...navyBtn, display: 'flex', alignItems: 'center', gap: 6,
          opacity: saving ? 0.6 : 1,
          background: saved ? '#16a34a' : AMBER,
        }}>
          <Save size={14} /> {saving ? 'Guardando…' : saved ? 'Guardado' : 'Guardar Cambios'}
        </button>
      </div>

      {SETTING_GROUPS.map(group => (
        <div key={group.title} style={{ ...bubbleStyle, marginBottom: 16 }}>
          <h3 style={{ fontSize: 15, fontWeight: '700', color: NAVY, marginBottom: 4 }}>{group.title}</h3>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{group.description}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {group.keys.map(({ key, label, placeholder, type, helper }) => (
              <div key={key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: '600', color: '#475569', marginBottom: 4 }}>
                  {label}
                </label>
                <input
                  type={type || 'text'}
                  value={settings[key] || ''}
                  onChange={e => handleChange(key, e.target.value)}
                  placeholder={placeholder}
                  style={fieldStyle}
                />
                {helper && (
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{helper}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ ...bubbleStyle, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: '700', color: NAVY, marginBottom: 4 }}>Nómina de oficina</h3>
        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Revisores de Nómina de oficina: reciben el aviso cuando el personal de Oficina somete sus
          horas, sea cual sea el proyecto. Sin ninguno marcado, esas horas no se pueden someter.
        </p>
        {revisores.length === 0 && yaNoValen.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8' }}>No hay usuarios con el rol Revisión Final.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 18px' }}>
            {revisores.map(r => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: NAVY, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={idsDe(settings[CLAVE_REVISORES_OFICINA]).includes(r.id)}
                  onChange={() => toggleRevisorOficina(r.id)}
                />
                {r.name}
              </label>
            ))}
            {yaNoValen.map(r => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#b91c1c', cursor: 'pointer' }}>
                <input type="checkbox" checked onChange={() => toggleRevisorOficina(r.id)} />
                {r.name} (inactivo o sin Revisión Final — desmárcalo para poder guardar)
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
